import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import type { PageElementUnit } from '@streamlain/database-identity';
import {
  createWriterShardPrefix,
  generateFractionalIndexingKeyBetween,
  PAGE_DOCUMENT_PART_ID,
  PAGE_TREE_DELETED_ID,
  PAGE_TREE_RECOVERED_ID,
  type PageStructureListener,
  pageTreeChildIds,
  planPageBlockPart,
  planPageElementPart,
  readPageFormatVersion,
} from '@streamlain/database-identity';
import * as Y from 'yjs';

import { Boxed, isPureObject, native2Y } from '../../reactive/index.js';
import type { Schema } from '../../schema/index.js';
import type { BlockModel } from '../block/block-model.js';
import type { YBlock } from '../block/types.js';
import { assertValidXYWHPropPath } from '../block/xywh-validation.js';
import { type BlockStructureProps, internalPrimitives } from '../block/zod.js';
import { type PageStructure, pageStructureOf } from './page-structure.js';
import type { Store } from './store.js';

/**
 * A canvas element to place on a block, as its transformer reads it from a
 * snapshot: its fields without layer index and members, its layer index and
 * the members of a group.
 */
export type PageElementInput = {
  id: string;
  fields: Y.Map<unknown>;
  rect: readonly [number, number, number, number];
  order: string | null;
  members: readonly string[];
};

type StructureValues = {
  members?: string[];
  presentationIndex?: string;
  elements?: readonly PageElementInput[];
};

/** Takes the props a block keeps in the page structure out of its fields. */
function takeStructureProps(
  structure: BlockStructureProps | undefined,
  props: Record<string, unknown>
): StructureValues {
  const values: StructureValues = {};
  if (!structure) return values;
  const { members, presentationIndex, elements } = structure;
  if (members !== undefined) {
    const value = props[members];
    if (isPureObject(value)) values.members = Object.keys(value);
    delete props[members];
  }
  if (presentationIndex !== undefined) {
    const value = props[presentationIndex];
    if (typeof value === 'string') values.presentationIndex = value;
    delete props[presentationIndex];
  }
  if (elements !== undefined) {
    const value = props[elements];
    const list = value instanceof Boxed ? value.getValue() : value;
    if (Array.isArray(list)) values.elements = list as PageElementInput[];
    delete props[elements];
  }
  return values;
}

const DATABASE_ROOT_OWNED_PROPS = new Set([
  'activeViewId',
  'allowDelete',
  'canvasPresentation',
  'cells',
  'columns',
  'comments',
  'databaseShardManifest',
  'managedBy',
  'schemaLocked',
  'systemManaged',
  'title',
  'views',
]);

const DATABASE_HOST_OWNED_PROPS = new Set([
  'docScoped',
  'fileIndexLastXYWH',
  'index',
  'lockedBySelf',
  'visible',
  'xywh',
]);

function filterShardedDatabaseDefaults(
  flavour: string,
  props: Record<string, unknown>,
  explicitProps: Record<string, unknown>
) {
  if (
    flavour !== 'affine:database' ||
    typeof props.databaseRootDocId !== 'string' ||
    !props.databaseRootDocId
  ) {
    return;
  }

  const isStorageRoot = props.databaseShardManifest !== undefined;
  const forbiddenDefaults = isStorageRoot
    ? DATABASE_HOST_OWNED_PROPS
    : DATABASE_ROOT_OWNED_PROPS;

  forbiddenDefaults.forEach(key => {
    delete props[key];
  });
  if (!isStorageRoot) {
    DATABASE_HOST_OWNED_PROPS.forEach(key => {
      if (!(key in explicitProps)) {
        delete props[key];
      }
    });
  }
}

/**
 * Block CRUD of a page store. Placement is structure entries only: every
 * write goes through the page's structure writer, and parent, siblings and
 * root are read from the tree its index derives once per change.
 */
export class DocCRUD {
  private readonly _structure: PageStructure;

  get root(): string | null {
    return this._structure.tree().root;
  }

  constructor(
    private readonly _store: Store,
    private readonly _schema: Schema
  ) {
    this._structure = pageStructureOf(_store);
  }

  /** The structure of the page this store shows. */
  get structure(): PageStructure {
    return this._structure;
  }

  private get _entries() {
    return this._structure.index.entries();
  }

  private get _writer() {
    return this._structure.writer;
  }

  private _getSiblings<T>(
    id: string,
    fn: (index: number, siblings: string[]) => T
  ) {
    const parentId = this.getParent(id);
    if (!parentId) return null;

    const siblings = this.childIds(parentId);
    const index = siblings.indexOf(id);
    if (index === -1) return null;

    return fn(index, siblings);
  }

  /** Children of a block in reading order. */
  childIds(id: string): string[] {
    return pageTreeChildIds(this._structure.tree(), id);
  }

  /** Calls `listener` after every transaction that changed the structure. */
  onStructureChange(listener: PageStructureListener): () => void {
    return this._structure.subscribe(listener);
  }

  addBlock(
    id: string,
    flavour: string,
    initialProps: Record<string, unknown> = {},
    parent?: string | null,
    parentIndex?: number
  ) {
    const schema = this._schema.flavourSchemaMap.get(flavour);
    if (!schema) {
      throw new BlockSuiteError(
        ErrorCode.ModelCRUDError,
        `schema for flavour: ${flavour} not found`
      );
    }

    this._structure.assertBlockIdLoaded(id);
    const entry = this._entries.get(id);
    if (entry && entry.p !== PAGE_TREE_DELETED_ID) {
      throw new BlockSuiteError(
        ErrorCode.ModelCRUDError,
        `Should not add existing block: ${id}`
      );
    }

    const parentFlavour = parent
      ? this._structure.blockFields(parent)?.get('sys:flavour')
      : undefined;

    this._schema.validate(flavour, parentFlavour as string);

    const version = schema.version;
    const children = (
      initialProps.children as undefined | (string | BlockModel)[]
    )?.map(child => (typeof child === 'string' ? child : child.id));

    const defaultProps = schema.model.props?.(internalPrimitives) ?? {};
    const props = {
      ...defaultProps,
      ...initialProps,
    };
    filterShardedDatabaseDefaults(flavour, props, initialProps);

    delete props.id;
    delete props.flavour;
    delete props.children;
    const structureValues = takeStructureProps(schema.model.structure, props);

    const isFlatData = schema.model.isFlatData;
    const validateProps = (obj: unknown, basePath: string) => {
      if (isPureObject(obj)) {
        Object.entries(obj).forEach(([key, value]) => {
          const fullPath = basePath ? `${basePath}.${key}` : key;
          validateProps(value, fullPath);
        });
        return;
      }

      assertValidXYWHPropPath({
        blockId: id,
        flavour,
        path: basePath,
        value: obj,
        allowUndefined: true,
        enforceWorld: this._store.canvasWorld === 'bounded',
      });
    };
    validateProps(props, '');

    const isRoot = schema.model.role === 'root';
    const parentId = parent ?? (isRoot ? null : this.root);
    const plan = entry
      ? { partId: entry.s, created: false }
      : parentId === null || schema.model.documentPart
        ? { partId: PAGE_DOCUMENT_PART_ID, created: false }
        : planPageBlockPart({
            formatVersion: readPageFormatVersion(this._structure.docs.head),
            entries: this._entries,
            directory: this._structure.planningDirectory(),
            parentId: parentId ?? '',
            siblings:
              parentId === null
                ? []
                : (this._structure.tree().children.get(parentId) ?? []),
            index: parentIndex,
            writerPrefix: createWriterShardPrefix(
              this._structure.docs.head.clientID
            ),
          });
    this._structure.docs.createPart(plan.partId);
    const blocks = this._structure.docs.partBlocks(plan.partId)!;
    blocks.doc!.transact(() => {
      const before = Y.encodeStateVector(blocks.doc!);
      const yBlock = new Y.Map() as YBlock;
      blocks.set(id, yBlock);

      yBlock.set('sys:id', id);
      yBlock.set('sys:flavour', flavour);
      yBlock.set('sys:version', version);

      if (isFlatData) {
        const run = (obj: unknown, basePath: string) => {
          if (isPureObject(obj)) {
            Object.entries(obj).forEach(([key, value]) => {
              const fullPath = basePath ? `${basePath}.${key}` : key;
              run(value, fullPath);
            });
          } else {
            yBlock.set(`prop:${basePath}`, native2Y(obj));
          }
        };
        run(props, '');
      } else {
        Object.entries(props).forEach(([key, value]) => {
          if (value === undefined) return;

          yBlock.set(`prop:${key}`, native2Y(value));
        });
      }

      // A block without a placed parent keeps its fields unplaced, as a block
      // added before its page root.
      if (parentId === null ? !isRoot : !this._entries.has(parentId)) {
        this._structure.recordPartDelta(plan.partId, before);
        return;
      }

      this._writer.placeBlock({
        id,
        parentId,
        index: parentIndex,
        partId: plan.partId,
      });
      this._structure.placed(id);
      const placedChildren = children?.filter(child =>
        this._entries.has(child)
      );
      if (placedChildren?.length) {
        this._writer.moveBlocks(placedChildren, id);
      }
      this._structure.recordPartDelta(plan.partId, before);
      this._applyStructureValues(id, structureValues);
    }, this._structure.partWriteOrigin(blocks.doc!));
  }

  private _applyStructureValues(id: string, values: StructureValues) {
    if (values.presentationIndex !== undefined) {
      this._writer.setPresentationIndex(id, values.presentationIndex);
    }
    values.members?.forEach(member => this._structure.join(member, id));
    // Elements without a layer index go on top, in snapshot order.
    const elements = values.elements ?? [];
    for (const element of [
      ...elements.filter(({ order }) => order !== null),
      ...elements.filter(({ order }) => order === null),
    ]) {
      this.placeElement(
        id,
        element.id,
        element.fields,
        element.order,
        element.rect
      );
      element.members.forEach(member =>
        this._structure.join(member, element.id)
      );
    }
  }

  /** Plans and stores a canvas element before placing it on its surface. */
  placeElement(
    surfaceId: string,
    id: string,
    fields: Y.Map<unknown>,
    order: string | null,
    rect: readonly [number, number, number, number],
    unit?: PageElementUnit
  ) {
    // Without a layer index the element goes on top of the surface.
    const top = this._structure.tree().elements.get(surfaceId)?.at(-1);
    const doc = this._structure.docs.head;
    const plan = planPageElementPart({
      formatVersion: readPageFormatVersion(doc),
      rect,
      unit,
      entries: this._entries,
      directory: this._structure.planningDirectory(),
      writerPrefix: createWriterShardPrefix(doc.clientID),
    });
    const partId = this._entries.get(id)?.s ?? plan.partId;
    this._structure.docs.createPart(partId);
    const elements = this._structure.docs.partElements(partId)!;
    elements.doc!.transact(() => {
      const before = Y.encodeStateVector(elements.doc!);
      elements.set(id, fields);
      this._writer.placeElement({
        id,
        surfaceId,
        order:
          order ??
          generateFractionalIndexingKeyBetween(
            top === undefined ? null : this._entries.get(top)!.o,
            null
          ),
        partId,
      });
      this._structure.placed(id);
      this._structure.recordPartDelta(partId, before);
    }, this._structure.partWriteOrigin(elements.doc!));
  }

  deleteBlock(
    id: string,
    options: {
      bringChildrenTo?: string;
      deleteChildren?: boolean;
    } = {
      deleteChildren: true,
    }
  ) {
    const { bringChildrenTo, deleteChildren } = options;
    if (bringChildrenTo && deleteChildren) {
      console.error(
        'Cannot bring children to another block and delete them at the same time'
      );
      return;
    }

    if (!this._structure.blockFields(id)) return;
    if (!this.getParent(id)) return;

    if (bringChildrenTo) {
      const model = this._structure.blockFields(bringChildrenTo);
      if (!model || !this._entries.has(bringChildrenTo)) {
        this._writer.removeBlock(id);
        return;
      }
      const bringFlavour = model.get('sys:flavour');
      this.childIds(id).forEach(child => {
        const childModel = this._structure.blockFields(child);
        if (!childModel) return;
        this._schema.validate(
          childModel.get('sys:flavour') as string,
          bringFlavour as string
        );
      });
      this._writer.deleteBlock(id, { bringChildrenTo });
      return;
    }

    if (deleteChildren) {
      this._writer.deleteBlock(id);
      return;
    }

    // The children stay for the caller, who places them under another block.
    this._writer.removeBlock(id);
  }

  getNext(id: string) {
    return this._getSiblings(
      id,
      (index, siblings) => siblings.at(index + 1) ?? null
    );
  }

  getParent(targetId: string): string | null {
    const tree = this._structure.tree();
    const parent = tree.parentOf.get(targetId);
    if (parent === undefined) return null;
    // Recovered entries stand at the end of the root's children.
    return parent === PAGE_TREE_RECOVERED_ID ? tree.root : parent;
  }

  getPrev(id: string) {
    return this._getSiblings(id, (index, siblings) =>
      index === 0 ? null : (siblings.at(index - 1) ?? null)
    );
  }

  moveBlocks(
    blocksToMove: string[],
    newParent: string,
    targetSibling: string | null = null,
    shouldInsertBeforeSibling = true
  ) {
    if (
      blocksToMove.length > 1 &&
      targetSibling &&
      blocksToMove.includes(targetSibling)
    ) {
      console.error(
        'Cannot move blocks when the target sibling is in the blocks to move'
      );
      return;
    }

    if (blocksToMove.length === 1 && targetSibling === blocksToMove[0]) {
      return;
    }

    if (blocksToMove.includes(newParent)) {
      console.error(
        'Cannot move blocks when the new parent is in the blocks to move'
      );
      return;
    }

    // A map to store parent block and their respective child blocks
    const childBlocksPerParent = new Map<string, string[]>();

    const parentBlock = this._structure.blockFields(newParent);
    if (!parentBlock || !this._entries.has(newParent)) return;

    const parentFlavour = parentBlock.get('sys:flavour');

    blocksToMove.forEach(blockId => {
      const parent = this.getParent(blockId);
      if (!parent) return;

      const block = this._structure.blockFields(blockId);
      if (!block) return;

      this._schema.validate(
        block.get('sys:flavour') as string,
        parentFlavour as string
      );

      const children = childBlocksPerParent.get(parent);
      if (!children) {
        childBlocksPerParent.set(parent, [blockId]);
        return;
      }

      const last = children[children.length - 1];
      if (this.getNext(last) !== blockId) {
        throw new BlockSuiteError(
          ErrorCode.ModelCRUDError,
          'The blocks to move are not contiguous under their parent'
        );
      }

      children.push(blockId);
    });

    const moving = [...childBlocksPerParent.values()].flat();
    if (moving.length === 0) return;
    if (
      targetSibling !== null &&
      !this.childIds(newParent).includes(targetSibling)
    ) {
      console.error('Target sibling not found, just insert to the end');
    }
    this._writer.moveBlocks(
      moving,
      newParent,
      targetSibling,
      shouldInsertBeforeSibling
    );
  }

  /** Puts `children` under the block in this order, as one reorder. */
  updateBlockChildren(id: string, children: string[]) {
    if (!this._structure.blockFields(id) || !this._entries.has(id)) return;
    const placed = children.filter(child => this._entries.has(child));
    if (placed.length > 0) this._writer.moveBlocks(placed, id);
  }
}
